import React, { useState, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
    CogIcon,
    DocumentTextIcon,
    ArrowPathIcon,
    CheckCircleIcon,
    ListBulletIcon,
    PencilIcon,
    XMarkIcon,
    ChevronLeftIcon,
    TableCellsIcon
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import toast from 'react-hot-toast';
import apiClient from '../../services/apiClient';

const PhaseDashboard: React.FC = () => {
    const { customerId, phaseId } = useParams();
    const queryClient = useQueryClient();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ name: '', description: '', ftsd: '' });

    const { data: customer } = useQuery({
        queryKey: ['customer', customerId],
        queryFn: () => apiClient.getCustomer(customerId!) as Promise<{ name: string }>,
        enabled: !!customerId
    });

    const { data: phases } = useQuery({
        queryKey: ['phases', customerId],
        queryFn: () => apiClient.getPhases(customerId!) as Promise<any[]>,
        enabled: !!customerId
    });

    const phase = phases?.find(p => p.id === phaseId);

    const openEditModal = () => {
        if (phase) {
            setEditForm({
                name: phase.name,
                description: phase.description || '',
                ftsd: phase.ftsd || ''
            });
            setIsEditOpen(true);
        }
    };

    const handleUpdate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!customerId || !phaseId) return;

        try {
            await apiClient.updatePhase(customerId, phaseId, {
                name: editForm.name,
                description: editForm.description,
                ftsd: editForm.ftsd
            });
            toast.success('Phase updated successfully');
            queryClient.invalidateQueries({ queryKey: ['phases', customerId] });
            setIsEditOpen(false);
        } catch (err: any) {
            console.error(err);
            toast.error('Failed to update phase');
        }
    };

    const tools = [
        {
            to: 'wizard',
            icon: ListBulletIcon,
            label: 'Wizard',
            description: 'Guided step-by-step migration workflow.'
        },
        {
            to: 'credentials',
            icon: CogIcon,
            label: 'Credentials',
            description: 'Configure source and target system access credentials.'
        },
        {
            to: 'entities',
            icon: ListBulletIcon,
            label: 'Entities',
            description: 'Select which entities to include in this migration phase.'
        },
        {
            to: 'metadata',
            icon: DocumentTextIcon,
            label: 'Metadata',
            description: 'View and manage entity metadata definitions.'
        },
        {
            to: 'files',
            icon: DocumentTextIcon,
            label: 'Files',
            description: 'Upload and manage implementation files.'
        },
        {
            to: 'mapping-v3',
            icon: ListBulletIcon,
            label: 'Field Mapping',
            description: 'Step-by-step guided mapping with identity resolution.'
        },
        {
            to: 'validation',
            icon: CheckCircleIcon,
            label: 'Validation',
            description: 'Validate data integrity and mapping rules.'
        },
        {
            to: 'export',
            icon: ArrowPathIcon,
            label: 'Export',
            description: 'Generate and download the final migration package.'
        },
    ];

    if (!customer || !phase) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="loading-spinner w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-7xl mx-auto">
            {/* Breadcrumb Navigation */}
            <nav className="mb-4 flex items-center text-sm text-gray-600">
                <Link to="/customers" className="hover:text-primary-600 transition-colors">
                    Customers
                </Link>
                <span className="mx-2">/</span>
                <Link
                    to={`/customers/${customerId}/phases`}
                    className="hover:text-primary-600 transition-colors"
                >
                    {customer.name}
                </Link>
                <span className="mx-2">/</span>
                <span className="text-gray-900 font-medium">{phase.name}</span>
            </nav>

            {/* Back Button & Header */}
            <div className="mb-8">
                <Link
                    to={`/customers/${customerId}/phases`}
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium mb-4 transition-colors"
                >
                    <ChevronLeftIcon className="w-4 h-4 mr-1" />
                    Back to Phases
                </Link>

                <div className="flex justify-between items-start">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-3xl font-bold text-gray-900">{phase.name}</h1>
                            <button
                                onClick={openEditModal}
                                className="p-1.5 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded-full transition-colors"
                                title="Edit Phase Details"
                            >
                                <PencilIcon className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex flex-col gap-1 text-gray-600">
                            <p>
                                <span className="font-medium text-gray-900">{customer.name}</span>
                                <span className="mx-2">•</span>
                                Migration Phase Dashboard
                            </p>
                            {phase.ftsd && (
                                <p className="text-sm">
                                    <span className="font-medium text-gray-700">FTSD:</span> {new Date(phase.ftsd).toLocaleDateString()}
                                </p>
                            )}
                            {phase.description && (
                                <p className="text-sm mt-1 max-w-2xl">{phase.description}</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tools.map((tool) => (
                    <Link
                        key={tool.to}
                        to={`/customers/${customerId}/phases/${phaseId}/${tool.to}`}
                        className="group block p-6 bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200"
                    >
                        <div className="flex items-start">
                            <div className="p-3 rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-200">
                                <tool.icon className="w-8 h-8" />
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                                    {tool.label}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500">
                                    {tool.description}
                                </p>
                            </div>
                        </div>
                    </Link>
                ))}
            </div>

            <Transition appear show={isEditOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setIsEditOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <div className="flex justify-between items-center mb-4">
                                        <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                                            Edit Phase Details
                                        </Dialog.Title>
                                        <button
                                            onClick={() => setIsEditOpen(false)}
                                            className="text-gray-400 hover:text-gray-500"
                                        >
                                            <XMarkIcon className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleUpdate} className="space-y-4">
                                        <div>
                                            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                                Phase Name
                                            </label>
                                            <input
                                                type="text"
                                                id="name"
                                                value={editForm.name}
                                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                                                Description
                                            </label>
                                            <textarea
                                                id="description"
                                                rows={3}
                                                value={editForm.description}
                                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label htmlFor="ftsd" className="block text-sm font-medium text-gray-700">
                                                Full Transmission Start Date (FTSD)
                                            </label>
                                            <input
                                                type="date"
                                                id="ftsd"
                                                value={editForm.ftsd}
                                                onChange={(e) => setEditForm({ ...editForm, ftsd: e.target.value })}
                                                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm"
                                            />
                                            <p className="mt-1 text-xs text-gray-500">
                                                The date when full data transmission begins.
                                            </p>
                                        </div>

                                        <div className="mt-6 flex justify-end gap-3">
                                            <button
                                                type="button"
                                                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                                onClick={() => setIsEditOpen(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
};

export default PhaseDashboard;
